import Coroutiner from '../utils/Coroutiner';
import GameOptions from '../utils/GameOptions';
import MKEvent from '../utils/MKEvent';
import ParallelActions from '../utils/ParallelActions';
import SequencedActions from '../utils/SequencedActions';
// Effects
import FountainEffect from '../effects/FountainEffect';
// Controllers
import GuiController from '../controllers/GuiController';
import SlotControls from '../controllers/SlotControls';
import SoundController from '../controllers/SoundController';
import WinController from '../controllers/WinController';
// States
import FreeInputWinState from '../states/FreeInputWinState';
import GameOverState from '../states/GameOverState';
import IddleState from '../states/IddleState';
import LoseState from '../states/LoseState';
import PreSpinState from '../states/PreSpinState';
import SpinState from '../states/SpinState';
import StateMachine from '../states/StateMachine';
import WinState from '../states/WinState';
// Components
import PayLine from '../components/Payline';
import SlotPlayer from '../components/SlotPlayer';
// Scenes
import BaseScene from './BaseScene';

export class SlotScene extends BaseScene {
	constructor() {
		super({ key: 'SlotScene' });
	}

	create() {
		this.updateEvent = new MKEvent();
		// 0) events
		this.endWinCalcEvent = new MKEvent();
		this.winCoinsEvent = new MKEvent();
		this.endFreeGamesEvent = new MKEvent();
		this.freeSpinWinEvent = new MKEvent();
		this.startFreeGamesEvent = new MKEvent();

		// 1) main properties
		this.centerX = this.game.config.width / 2 + GameOptions.localOffsetX;
		this.centerY = this.game.config.height / 2 + GameOptions.localOffsetY;
		this.useWild = GameOptions.useWild && GameOptions.hasOwnProperty('wild') && GameOptions.wild !== null;
		this.isCascadeSpin = false; // cascade spin flag (used only in cascade slot)
		this.sumCascadeCoins = 0; // used only in cascade slot
		this.isFreeSpin = false; // free spin flag
		this.reelSpin = false; // reel spin flag
		this.playFreeSpins = false;
		this.startFreeGames = false;
		this.endFreeGames = false;
		this.cTime = 0;
		this.useWildInFirstPosition = GameOptions.useWildInFirstPosition;
		this.useLineBetMultiplier = GameOptions.useLineBetMultiplier;
		this.useLineBetFreeSpinMultiplier = GameOptions.useLineBetFreeSpinMultiplier;
		this.symbolsDict = {};
		GameOptions.symbols.forEach(s => {
			if (s.fileName != null) {
				this.symbolsDict[s.name] = s;
			}
		});
		this.spinCount = 0;

		// 2) pay tables
		this.payTable = [];
		GameOptions.payLines.forEach(pLine => {
			this.payTable.push(new PayLine(this, pLine.line, pLine.pay, pLine.freeSpins, GameOptions.wild));
		});
		this.payTableFull = this.createFullPaytable(this.payTable, this.useWild);
		this.scatterPayTable = [];

		// 3) create slot graphic
		GameOptions.createSlotGraphic(this);

		// 4) main objects
		this.slotPlayer = new SlotPlayer(GameOptions.defaultCoins); // default coins
		this.reels = GameOptions.createReels(this);
		this.lineButtons = GameOptions.createLineButtons ? GameOptions.createLineButtons(this) : null; // add line buttons - optional
		this.soundController = new SoundController(this);
		this.guiController = new GuiController(this);
		this.slotControls = new SlotControls(this, this.slotPlayer, GameOptions.lines, GameOptions.lineColor, GameOptions.lineBetMaxValue);
		this.winController = new WinController(
			this,
			this.slotControls.linesController,
			GameOptions.useScatter,
			GameOptions.scatter,
			GameOptions.winShowTime
		);

		// 5) add sounds
		this.box_click_clip = this.sound.add('box_click_clip');
		this.win_clip = this.sound.add('win_clip');
		this.button_click = this.sound.add('button_click');
		this.spin_clip = this.sound.add('spin_clip');
		this.wincoins_clip = this.sound.add('wincoins_clip');
		this.lose_clip = this.sound.add('lose_clip');

		// 6) controls
		GameOptions.createControls(this, this.slotControls);
		this.slotControls.init(GameOptions.selectedLines, true);

		// 7) state machine
		this.stateMachine = new StateMachine();
		this.iddleState = new IddleState(this, this.stateMachine);
		this.winState = new WinState(this, this.stateMachine);
		this.loseState = new LoseState(this, this.stateMachine);
		this.spinState = new SpinState(this, this.stateMachine);
		this.preSpinState = new PreSpinState(this, this.stateMachine);
		this.freeInputWinState = new FreeInputWinState(this, this.stateMachine);
		this.gameOverState = new GameOverState(this, this.stateMachine);
		this.stateMachine.initialize(this.iddleState);
		this.endWinCalcEvent.add(win => {
			if (win) {
				this.stateMachine.changeState(this.winState);
				this.lampsBlink(true);
			} else if (this.slotPlayer.winCoins === 0 && this.slotPlayer.coins === 0) {
				this.stateMachine.changeState(this.gameOverState);
			} else {
				this.stateMachine.changeState(this.loseState);
			}
		}, this);

		// 8) coroutines
		this.loseCorout = null;
		this.winCorout = null;
		this.freeInputWinCorout = null;

		// 9) Add Animations
		this.fountain = new FountainEffect(this, this.centerX, this.centerY + 100, 'coinspin', 5000, 100);
	}

	spinReels(reels, _slotConfig, completeCallback) {
		var pA = new ParallelActions();
		var ri = 0;
		reels.forEach(r => {
			pA.add(callBack => {
				var rand =
					_slotConfig.reels_simulate && _slotConfig.reels_simulate[ri] >= 0 ? _slotConfig.reels_simulate[ri] : r.getRandomOrderPosition();
				r.spin(rand, () => {
					callBack();
				});
				ri++;
			});
		});
		pA.start(completeCallback);
	}

	runSlot() {
		// 0) prepare, set flags
		let anyWin = false;
		let lineCoins = 0; // lines win coins
		let scatterCoins = 0; // scatter win coins
		let jpCoins = 0; // jack pot win coins
		let winSpins = 0;
		this.isCascadeSpin = false;
		this.reelSpin = true;
		if (this.slotControls.auto && !this.isFreeSpin) {
			this.slotControls.incAutoSpinsCounter();
		}
		this.slotPlayer.setWinCoinsCount(0);
		this.slotControls.linesController.hideAllLines();
		if (this.loseCorout !== null) {
			this.loseCorout.stop();
		}
		if (this.winCorout !== null) {
			this.winCorout.stop();
		}
		if (this.freeInputWinCorout !== null) {
			this.freeInputWinCorout.stop();
		}
		this.lampsBlink(false);

		// 1) start spin sound
		this.soundController.stopAll();
		this.soundController.playClip('spin_clip', true);

		// 2) create spin sequence
		this.spinCount++;
		var sA = new SequencedActions();
		sA.add(callBack => {
			this.spinReels(this.reels, GameOptions, callBack);
		}, this);
		sA.add(callBack => {
			console.log('spin complete');
			this.soundController.stopAll();
			callBack();
		}, this);

		sA.add(callBack => {
			this.winController.searchWinSymbols();
			callBack();
		}, this); // search spin result

		sA.add(callBack => {
			anyWin = this.winController.hasAnyWinn();
			if (anyWin) {
				lineCoins = this.winController.getLineWinCoins();
				scatterCoins = this.winController.getScatterWinCoins();
				if (this.useLineBetMultiplier) {
					lineCoins *= this.slotControls.lineBet;
					scatterCoins *= this.slotControls.lineBet;
				}
				var summCoins = jpCoins + lineCoins + scatterCoins;
				if (summCoins > 0)
					this.winCoinsEvent.events.forEach(eW => {
						if (eW != null && eW.action != null) {
							eW.action.call(eW.context, summCoins);
						}
					});

				winSpins = this.winController.getWinSpins();
				if (this.useLineBetFreeSpinMultiplier) {
					winSpins *= this.slotControls.lineBet;
				}
				if (winSpins > 0) {
					this.freeSpinWinEvent.events.forEach(eW => {
						if (eW != null && eW.action != null) {
							eW.action.call(eW.context, winSpins);
						}
					});
				}
			} else {
				this.isCascadeSpin = false;
			}
			this.endWinCalcEvent.events.forEach(eW => {
				if (eW != null && eW.action != null) {
					eW.action.call(eW.context, anyWin);
				}
			}); // start win show or loose show
			callBack();
		}, this);

		sA.add(callBack => {
			callBack();
		}, this);

		sA.start();
	}

	createFullPaytable(payTable, useWild) {
		var payTableFull = [];
		for (var j = 0; j < payTable.length; j++) {
			payTableFull.push(payTable[j]);
			var wildLines = payTable[j].getWildLines();
			if (useWild) {
				wildLines.forEach(wl => {
					payTableFull.push(wl);
				});
			}
		}
		return payTableFull;
	}

	addSpriteLocPos(name, posX, posY) {
		return this.add.sprite(this.centerX + posX, this.centerY + posY, name);
	}

	lampsBlink(blink) {
		if (blink && !this.lampsIntervalID) {
			this._lampsOn = false;
			this.lampsIntervalID = setInterval(() => {
				this.lampsArray.forEach(l => {
					l.setOn(this._lampsOn);
				});
				this._lampsOn = !this._lampsOn;
			}, 1000);
		} else if (!blink && this.lampsIntervalID) {
			clearInterval(this.lampsIntervalID);
			this.lampsArray.forEach(l => {
				l.setOn(true);
			});
			this.lampsIntervalID = null;
		}
	}

	winShow(completeCallBack) {
		this.miniGame = null;
		this.winCorout = new Coroutiner(this, this.winShowC(completeCallBack));
		this.winCorout.start();
	}

	// win show coroutine
	*winShowC(completeCallBack) {
		console.log('win show start: ' + this.spinCount);
		//3a ------ any win show event -------
		while (this.miniGame !== null || !this.guiController.hasNoPopUp()) {
			console.log('wait: this.miniGame and popups:  ' + this.miniGame + ' : ' + this.guiController.hasNoPopUp());
			yield null;
		}

		// 3b0 ---- show particles, line flasing  -----------
		let winShowEnd = false;
		this.winController.winSymbolShowOnce(() => {
			winShowEnd = true;
		});

		yield* this.wait_ms(1000);

		//3b1 --------- check Jack pot -------------
		while (this.miniGame !== null || !this.guiController.hasNoPopUp()) {
			yield null;
		}

		//3c0 -----------------calc coins -------------------
		let winCoins = this.winController.getLineWinCoins() + this.winController.getScatterWinCoins();
		if (this.useLineBetMultiplier) winCoins *= this.slotControls.lineBet;
		this.slotPlayer.setWinCoinsCount(winCoins);
		this.slotPlayer.addCoins(winCoins);
		while (this.miniGame !== null || !this.guiController.hasNoPopUp()) {
			yield null;
		}

		//3c1 ----------- calc free spins ----------------
		let winSpins = this.winController.getWinSpins();
		let winLinesCount = this.winController.winLines.length;
		if (this.useLineBetFreeSpinMultiplier) {
			winSpins *= this.slotControls.lineBet;
		}

		// win coins, big win, win spins sounds and messages
		//let bigWin = winCoins > 0 && winCoins >= this.slotPlayer.minWin && this.slotPlayer.useBigWinCongratulation;
		let bigWin = winCoins > 0 && winCoins >= GameOptions.minWin && GameOptions.useBigWinCongratulation;
		if (bigWin) {
			console.log('big win congratulation : ' + winCoins);
			this.showBigWinMessage(winCoins);
			this.fountain.start();
			this.soundController.playClip('wincoins_clip', false);
		} else if (winCoins > 0) {
			console.log('win coins congratulation : ' + winCoins);
			this.showWinCoinsMessage(winCoins, GameOptions.winMessageTime);
			this.soundController.playClip('wincoins_clip', false);
		}

		while (this.miniGame !== null || !this.guiController.hasNoPopUp()) {
			yield null;
		}

		if (winSpins > 0) {
			if (winCoins > 0) yield* this.wait_ms(1000); // delay between messages
			console.log('win free spins congratulation : ' + winSpins);
			this.showWinFreeSpinsMessage(winSpins);
			setTimeout(
				() => {
					this.soundController.playClip('win_clip', false);
				},
				winCoins > 0 ? 1500 : 100
			);
		}
		this.slotControls.addFreeSpins(winSpins);
		while (this.miniGame !== null || !this.guiController.hasNoPopUp()) {
			yield null;
		}

		this.playFreeSpins = this.slotControls.autoPlayFreeSpins && this.slotControls.hasFreeSpin();
		this.startFreeGames = !this.isFreeSpin && this.playFreeSpins;
		this.endFreeGames = this.isFreeSpin && !this.playFreeSpins;
		if (this.endFreeGames) this.endFreeGamesEvent.invoke();
		while (this.miniGame !== null || !this.guiController.hasNoPopUp()) {
			yield null;
		}

		//3d0 ----- invoke scatter win event -----------
		if (this.winController.scatterWin != null && winController.scatterWin.winEvent != null) {
			yield* this.wait_ms(1000);
			this.winController.scatterWin.winEvent.invoke();
			while (this.miniGame !== null || !this.guiController.hasNoPopUp()) {
				yield null;
			}
		}

		// 3d1 -------- add levelprogress --------------
		this.slotPlayer.addLevelProgress(
			this.useLineBetProgressMultiplier
				? this.winSpinLevelProgress * winLinesCount * this.slotControls.lineBet
				: this.winSpinLevelProgress * winLinesCount
		); // for each win line
		while (this.miniGame !== null || !this.guiController.hasNoPopUp()) {
			yield null;
		}

		// 3d2 ------------ start line events ----------
		//  this.winController.startLineEvents();
		while (this.miniGame !== null || !this.guiController.hasNoPopUp()) {
			yield null;
		}

		// start free games events
		if (this.startFreeGames) {
			var fgPU = this.guiController.showMessage('START FREE GAME', winSpins, this, () => {
				this.guiController.closePopUp(fgPU);
			});
			this.startFreeGamesEvent.invoke();
			while (this.miniGame !== null || !this.guiController.hasNoPopUp()) {
				yield null;
			}
		}

		//3e ---- ENABLE player interaction -----------
		this.reelsSpin = false;
		//  this.soundController.playMusic('');

		/*
        if (this.slotControls.auto && this.slotControls.autoSpinsCounter >= slotConfig.maxAutoSpins)
        {
            this.slotControls.resetAutoSpinsMode();
        }
        */
		while (!winShowEnd) {
			yield null;
		}

		//if (logStatistic) SlotStatistic.PrintStatistic();
		completeCallBack();
		console.log('win show end: ' + this.spinCount);
	}

	loseShow(completeCallBack) {
		this.loseCorout = new Coroutiner(this, this.loseShowC(completeCallBack));
		this.loseCorout.start();
	}

	// lose show coroutine
	*loseShowC(completeCallBack) {
		this.soundController.playClip('lose_clip', false);
		console.log('lose show, spinCount: ' + this.spinCount);
		console.log('play lose sound'); // play sound loseSound
		this.slotPlayer.addLevelProgress(this.loseSpinLevelProgress);
		this.playFreeSpins = this.slotControls.autoPlayFreeSpins && this.slotControls.hasFreeSpin();
		this.reelSpin = false;
		console.log('enable background music'); // restore background music
		console.log('wait for popups');
		/*
        if (this.slotControls.auto && this.slotControls.autoSpinsCounter >= this.slotControls.autoSpinCount)
        {
            this.slotControls.resetAutoSpinsMode();
        }
        */
		yield* this.wait_ms(1000); // wait 1 sec before call completeCallBack
		completeCallBack(this.slotControls.auto || this.playFreeSpins);
	}

	*wait_ms(ms) {
		var timeTarget = this.cTime + ms;
		while (timeTarget > this.cTime) {
			//  console.log('wait_ms: ' + (-this.cTime + timeTarget));
			yield timeTarget - this.cTime;
		}
	}

	freeInputWinShow(completeCallBack) {
		this.freeInputWinCorout = new Coroutiner(this, this.freeInputWinShowC(completeCallBack));
		this.freeInputWinCorout.start();
	}

	// win show coroutine while waiting for player action
	*freeInputWinShowC(completeCallBack) {
		yield* this.wait_ms(1000);
		let winShowEnd = false;
		this.winController.winSymbolShowOnce(() => {
			winShowEnd = true;
		});

		while (!winShowEnd) {
			//  console.log('wait win symbols show');
			yield null;
		}
		completeCallBack(this.slotControls.auto || this.playFreeSpins);
	}

	showWinCoinsMessage(winCoins, time) {
		var wMess = this.guiController.showMessage('Congratulation!', 'Your win: ' + winCoins + ' coins!', this, () => {
			if (this.timeoutMess) clearTimeout(this.timeoutMess);
			this.timeoutMess = null;
			this.guiController.closePopUp(wMess);
		});
		if (time && time > 0) {
			this.timeoutMess = setTimeout(() => {
				this.guiController.closePopUp(wMess);
			}, time);
		}
	}

	showBigWinMessage(winCoins) {
		var wMess = this.guiController.showMessage('Congratulation!', 'Big win: ' + winCoins + ' coins!', this, () => {
			this.guiController.closePopUp(wMess);
		});
	}

	showWinFreeSpinsMessage(winCoins) {
		var wMess = this.guiController.showMessage('Congratulation!', 'Your win: ' + winCoins + ' free spins!', this, () => {
			this.guiController.closePopUp(wMess);
		});
	}

	createGameMessagePUHandlerYNC(popup) {
		// add background and panel
		let backGround = popup.scene.add.sprite(0, 0, 'gray_background').setOrigin(0.5).setScale(300);
		backGround.setInteractive(); // block bottom controls
		popup.add(backGround);
		let panel = popup.scene.add.sprite(0, 0, 'message_panel').setOrigin(0.5);
		popup.add(panel);

		// add caption
		popup.captionText = popup.scene.add.bitmapText(0, -100, 'gameFont', 'Caption', 38, 1).setOrigin(0.5);
		popup.captionText.tint = 0xffffff;
		popup.add(popup.captionText);

		// add message
		popup.messageText = popup.scene.add.bitmapText(0, -30, 'gameFont', 'Message', 28, 1).setOrigin(0.5);
		popup.messageText.tint = 0xffffff;
		popup.add(popup.messageText);

		// add buttons
		popup.addButton('okButton', 'middle_button', 'middle_button_hover', false, -120, 230);
		popup.addButton('noButton', 'middle_button', 'middle_button_hover', false, 120, 230);
		popup.addButton('exitButton', 'exit_button', 'exit_button_hover', false, 245, -175);
		popup['okButton'].clickEvent.add(() => {
			popup.scene.soundController.playClip('button_click', false);
		}, popup);
		popup['noButton'].clickEvent.add(() => {
			popup.scene.soundController.playClip('button_click', false);
		}, popup);
		popup['exitButton'].clickEvent.add(() => {
			popup.scene.soundController.playClip('button_click', false);
		}, popup);

		// add button text
		popup.okText = popup.scene.add.bitmapText(-120, 227, 'gameFont', 'OK', 38, 1).setOrigin(0.5);
		popup.okText.tint = 0xffffff;
		popup.add(popup.okText);

		popup.noText = popup.scene.add.bitmapText(120, 227, 'gameFont', 'NO', 38, 1).setOrigin(0.5);
		popup.noText.tint = 0xffffff;
		popup.add(popup.noText);
	}

	// message with close button
	createGameMessagePUHandler(popup) {
		// add background and panel
		let backGround = popup.scene.add.sprite(0, 0, 'gray_background').setOrigin(0.5).setScale(300);
		backGround.setInteractive(); // block bottom controls
		popup.add(backGround);
		let panel = popup.scene.add.sprite(0, 0, 'message_panel').setOrigin(0.5);
		popup.add(panel);

		// add caption
		popup.captionText = popup.scene.add.bitmapText(0, -100, 'gameFont', 'Caption', 38, 1).setOrigin(0.5);
		popup.captionText.tint = 0xffffff;
		popup.add(popup.captionText);

		// add message
		popup.messageText = popup.scene.add.bitmapText(0, -30, 'gameFont', 'Message', 28, 1).setOrigin(0.5);
		popup.messageText.tint = 0xffffff;
		popup.add(popup.messageText);

		// add buttons
		popup.addButton('exitButton', 'exit_button', 'exit_button_hover', false, 245, -175);
		popup['exitButton'].clickEvent.add(() => {
			popup.scene.soundController.playClip('button_click', false);
		}, popup);
	}

	createGameMessagePUHandlerShare(popup) {
		// add background and panel
		let backGround = popup.scene.add.sprite(0, 0, 'gray_background').setOrigin(0.5).setScale(300);
		backGround.setInteractive(); // block bottom controls
		popup.add(backGround);
		let panel = popup.scene.add.sprite(0, 0, 'message_panel').setOrigin(0.5);
		popup.add(panel);

		// add caption
		popup.captionText = popup.scene.add.bitmapText(0, -120, 'gameFont', 'Caption', 38, 1).setOrigin(0.5);
		popup.captionText.tint = 0xffffff;
		popup.add(popup.captionText);

		// add message
		popup.messageText = popup.scene.add.bitmapText(0, -80, 'gameFont', 'Message', 28, 1).setOrigin(0.5);
		popup.messageText.tint = 0xffffff;
		popup.add(popup.messageText);

		// add message
		popup.pointsText = popup.scene.add.bitmapText(0, -30, 'gameFont', 'Points', 28, 1).setOrigin(0.5);
		popup.pointsText.tint = 0xffffff;
		popup.add(popup.pointsText);

		// add whatsapp button
		popup.addButton('whatsappButton', 'whatsapp_button', 'whatsapp_button_hover', false, -100, 60);
		popup['whatsappButton'].clickEvent.add(() => {
			popup.scene.soundController.playClip('button_click', false);
			popup.scene.share('whatsapp');
		}, popup);

		// add facebook button
		popup.addButton('facebookButton', 'facebook_button', 'facebook_button_hover', false, 0, 60);
		popup['facebookButton'].clickEvent.add(() => {
			popup.scene.soundController.playClip('button_click', false);
			popup.scene.share('facebook');
		}, popup);

		// add twitter button
		popup.addButton('twitterButton', 'twitter_button', 'twitter_button_hover', false, 100, 60);
		popup['twitterButton'].clickEvent.add(() => {
			popup.scene.soundController.playClip('button_click', false);
			popup.scene.share('twitter');
		}, popup);

		// add buttons
		popup.addButton('exitButton', 'exit_button', 'exit_button_hover', false, 245, -175);
		popup['exitButton'].clickEvent.add(() => {
			popup.scene.soundController.playClip('button_click', false);
		}, popup);
	}

	share(action) {
		gtag('event', 'click', { event_category: 'share', event_label: action });

		var loc = location.href;
		loc = loc.substring(0, loc.lastIndexOf('/') + 1);

		var title = '';
		var text = '';

		var shareTitle = 'Highscore on Slot Machine Game at [SCORE].';
		var shareMessage = '[SCORE] is mine new highscore on Slot Machine Game! Try it now!';

		title = shareTitle.replace('[SCORE]', this.slotPlayer.score);
		text = shareMessage.replace('[SCORE]', this.slotPlayer.score);

		var shareurl = '';

		if (action == 'twitter') {
			shareurl = 'https://twitter.com/intent/tweet?url=' + loc + '&text=' + text;
		} else if (action == 'facebook') {
			shareurl =
				'https://www.facebook.com/sharer/sharer.php?u=' +
				encodeURIComponent(
					loc + 'share.php?desc=' + text + '&title=' + title + '&url=' + loc + '&thumb=' + loc + 'share.jpg&width=590&height=300'
				);
		} else if (action == 'whatsapp') {
			shareurl = 'whatsapp://send?text=' + encodeURIComponent(text) + ' - ' + encodeURIComponent(loc);
		}

		window.open(shareurl);
	}

	update(time, delta) {
		this.cTime = time;
		GameOptions.simpleTweener.update(delta);
		this.updateEvent.events.forEach(eW => {
			if (eW != null && eW.action != null) eW.action.call(eW.context, time, delta);
		});

		// this.fpsText.text = 'fps: ' + Math.round(slotGame.loop.actualFps);
	}
}
